import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtEuro, fmtEuroCents } from "@/lib/format";
import { MONTHS } from "@/components/bizstats/KpiTab";
import { Search } from "lucide-react";
import { PriceAlerts } from "@/components/bizstats/PriceAlerts";

const chg = (v) => <span className={v > 0 ? "text-[#B5452F]" : v < 0 ? "text-[#6E7A63]" : "text-[#6F6A60]"}>{v > 0 ? "+" : ""}{v}%</span>;

const Detail = ({ it }) => (
  <tr className="bg-[#FAF8F4]" data-testid={`bs-price-detail-${it.item_code}`}>
    <td colSpan={6} className="px-3 py-2">
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase text-[#6F6A60]"><tr><th className="text-left py-1">Προμηθευτής</th>{MONTHS.map((m) => <th key={m} className="text-right px-1">{m}</th>)}<th className="text-right px-1">Μεταβολή</th></tr></thead>
        <tbody>
          {it.suppliers.map((s) => (
            <tr key={s.supplier} className="border-t border-[#E3DED4]">
              <td className="py-1 pr-2 max-w-[220px] truncate" title={s.supplier}>{s.supplier}</td>
              {MONTHS.map((_, i) => <td key={i} className="text-right px-1 tabular whitespace-nowrap">{s.months[String(i + 1)] ? fmtEuroCents(s.months[String(i + 1)]) : "—"}</td>)}
              <td className="text-right px-1 tabular">{chg(s.change_pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </td>
  </tr>
);

export const PricesTab = ({ year }) => {
  const [q, setQ] = useState("");
  const [cat1, setCat1] = useState("");
  const [open, setOpen] = useState(null);
  const { data } = useQuery({ queryKey: ["erp-prices", year, q, cat1], queryFn: async () => (await api.get("/erp/prices", { params: { year, q, cat1 } })).data });
  return (
    <div className="space-y-4" data-testid="bs-prices-tab">
      <PriceAlerts year={year} />
      <div className="flex flex-wrap gap-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-[#6F6A60]" />
          <input data-testid="bs-price-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Είδος ή προμηθευτής…"
            className="rounded-xl border border-[#E3DED4] bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-[#45423C] w-64" />
        </div>
        <select data-testid="bs-price-cat" value={cat1} onChange={(e) => setCat1(e.target.value)}
          className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C]">
          <option value="">Όλες οι κατηγορίες</option>
          {(data?.cat1_options || []).filter(Boolean).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="text-xs text-[#6F6A60] self-center" data-testid="bs-price-count">{data?.count || 0} είδη (εμφανίζονται έως 300, κατά αξία)</span>
      </div>
      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F0EA] text-[10px] uppercase tracking-wider text-[#6F6A60]">
            <tr><th className="px-3 py-2 text-left">Είδος</th><th className="px-3 text-left">Κατηγορία</th><th className="px-3 text-right">Ποσότητα</th><th className="px-3 text-right">Αξία</th><th className="px-3 text-right">Μέση τιμή</th><th className="px-3 text-right">Μεταβολή τιμής</th></tr>
          </thead>
          <tbody>
            {(data?.items || []).map((it) => (
              <React.Fragment key={it.item_code + it.item}>
                <tr className="border-t border-[#E3DED4] cursor-pointer hover:bg-[#FAF8F4]" data-testid={`bs-price-row-${it.item_code}`}
                  onClick={() => setOpen(open === it.item_code ? null : it.item_code)}>
                  <td className="px-3 py-2 font-medium text-[#45423C]">{it.item}<div className="text-[11px] text-[#6F6A60]">{it.suppliers.length} προμηθευτ{it.suppliers.length === 1 ? "ής" : "ές"}</div></td>
                  <td className="px-3 text-xs text-[#6F6A60]">{it.cat}</td>
                  <td className="px-3 text-right tabular">{new Intl.NumberFormat("el-GR", { maximumFractionDigits: 2 }).format(it.qty)}</td>
                  <td className="px-3 text-right tabular">{fmtEuro(it.net)}</td>
                  <td className="px-3 text-right tabular">{fmtEuroCents(it.avg_price)}</td>
                  <td className="px-3 text-right tabular">{chg(it.max_change_pct)}</td>
                </tr>
                {open === it.item_code && <Detail it={it} />}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[#6F6A60]">Τιμή μονάδας = καθαρή αξία ÷ ποσότητα, ανά μήνα. Μεταβολή = τελευταία έναντι πρώτης τιμής του έτους ανά προμηθευτή. Πατήστε ένα είδος για ανάλυση ανά προμηθευτή.</p>
    </div>
  );
};
